import { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid'; // Import uuid for unique IDs

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number; // Optional duration in ms
  actions?: { label: string; onClick: () => void; primary?: boolean }[]; // Optional actions
}

interface NotificationContextType {
  notifications: Notification[];
  showNotification: (type: NotificationType, message: string, duration?: number, actions?: Notification['actions']) => void;
  clearNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((type: NotificationType, message: string, duration: number = 5000, actions?: Notification['actions']) => {
    const id = uuidv4();
    const newNotification: Notification = {
      id,
      type,
      message,
      duration,
      actions,
    };
    setNotifications((prev) => [...prev, newNotification]);
  }, []);

  const clearNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, showNotification, clearNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};
